import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import Swal from 'sweetalert2';
import { PublicScheduleService } from '../../services/public-schedule.service';

declare var google: any;

@Component({
  selector: 'app-schedule-booking-page',
  templateUrl: './schedule-booking-page.component.html',
  styleUrls: ['./schedule-booking-page.component.scss']
})
export class ScheduleBookingPageComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('mapContainer') mapContainer: ElementRef;

  token = '';
  loading = true;
  submitting = false;
  cancelling = false;
  error: string = null;
  formError: string = null;
  success = false;

  clientName = '';
  hasDebt = false;
  debt = 0;
  subscriptions: any[] = [];
  calendar: any[] = [];
  bookings: any[] = [];

  selectedSubscriptionId: number = null;
  selectedDate: string = null;
  selectedSlot: string = null;
  currentMonth: Date = new Date();

  receiverName = '';
  receiverPhone = '';
  lat: number = -34.6037;
  lng: number = -58.3816;
  verifiedAddress = '';
  mapReady = false;

  private map: any;
  private marker: any;
  private geocoder: any;
  private mapInitAttempts = 0;

  constructor(
    private route: ActivatedRoute,
    private scheduleService: PublicScheduleService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.token = this.route.snapshot.paramMap.get('token') || '';
    this.load();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.tryInitMap(), 200);
  }

  ngOnDestroy(): void {
    this.marker = null;
    this.map = null;
  }

  reopenSchedule(): void {
    this.success = false;
    this.map = null;
    this.marker = null;
    this.mapReady = false;
    this.mapInitAttempts = 0;
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.scheduleService.getSchedule(this.token).subscribe({
      next: (res) => {
        this.clientName = res.client?.name || '';
        this.hasDebt = !!res.has_debt;
        this.debt = res.debt || 0;
        this.subscriptions = res.subscriptions || [];
        this.calendar = res.calendar || [];
        this.bookings = res.bookings || [];

        const withBooking = this.subscriptions.find((s) => s.active_booking);
        const bookable = this.subscriptions.find((s) => s.can_book);
        this.selectedSubscriptionId =
          withBooking?.subscription_id ||
          bookable?.subscription_id ||
          this.subscriptions[0]?.subscription_id ||
          null;

        this.applySubscriptionDefaults();
        this.formError = null;
        this.loading = false;
        this.cdr.detectChanges();
        setTimeout(() => this.tryInitMap(), 150);
      },
      error: (err) => {
        this.error = err?.error?.error || 'El link es inválido o ya venció.';
        this.loading = false;
      }
    });
  }

  get selectedSubscription(): any {
    return this.subscriptions.find(
      (s) => Number(s.subscription_id) === Number(this.selectedSubscriptionId)
    );
  }

  get activeBooking(): any {
    return this.selectedSubscription?.active_booking || null;
  }

  /** Dentro de plazo (hasta 2 días hábiles antes del ruteo) se puede editar/reprogramar/cancelar. */
  get canEditFully(): boolean {
    const booking = this.activeBooking;
    if (!booking) return true;
    return booking.can_edit !== false && booking.can_reschedule !== false;
  }

  get canModifyVisit(): boolean {
    return !!this.activeBooking && this.canEditFully;
  }

  get isVisitLocked(): boolean {
    return !!this.activeBooking && !this.canEditFully;
  }

  get canEditContactAndMap(): boolean {
    return !this.hasDebt && this.canEditFully;
  }

  get canCancelVisit(): boolean {
    const booking = this.activeBooking;
    if (!booking) return false;
    return booking.can_cancel !== false && this.canEditFully;
  }

  get confirmButtonLabel(): string {
    if (this.submitting) {
      return this.activeBooking ? 'Guardando...' : 'Agendando...';
    }
    if (this.isVisitLocked) return 'Visita bloqueada';
    if (!this.activeBooking) return 'Confirmar visita';
    return 'Guardar cambios';
  }

  get monthLabel(): string {
    return this.currentMonth.toLocaleDateString('es-AR', {
      month: 'long',
      year: 'numeric'
    });
  }

  get monthCells(): Array<{
    date: string;
    day: number;
    inMonth: boolean;
    meta: any;
  }> {
    const year = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();
    const first = new Date(year, month, 1);
    const startPad = first.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];

    for (let i = 0; i < startPad; i++) {
      cells.push({ date: '', day: 0, inMonth: false, meta: null });
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const date = `${year}-${String(month + 1).padStart(2, '0')}-${String(
        d
      ).padStart(2, '0')}`;
      const meta = this.calendar.find((c) => c.date === date) || null;
      cells.push({ date, day: d, inMonth: true, meta });
    }

    return cells;
  }

  onSubscriptionChange(id: number): void {
    this.selectedSubscriptionId = id;
    this.selectedDate = null;
    this.selectedSlot = null;
    this.applySubscriptionDefaults();
    this.updateMarkerPosition(true);
  }

  applySubscriptionDefaults(): void {
    const sub = this.selectedSubscription;
    if (!sub) return;

    const booking = sub.active_booking || null;
    this.verifiedAddress =
      (booking && booking.verified_address) || sub.address || '';

    if (booking) {
      this.receiverName = booking.receiver_name || '';
      this.receiverPhone = booking.receiver_phone || '';
      this.selectedDate = booking.scheduled_date || null;
      this.selectedSlot = booking.slot || null;
      if (this.selectedDate) {
        const parts = String(this.selectedDate).split('-');
        if (parts.length === 3) {
          this.currentMonth = new Date(
            Number(parts[0]),
            Number(parts[1]) - 1,
            1
          );
        }
      }
      if (booking.lat != null && booking.lng != null) {
        this.lat = Number(booking.lat);
        this.lng = Number(booking.lng);
      } else if (sub.lat && sub.lng) {
        this.lat = Number(sub.lat);
        this.lng = Number(sub.lng);
      }
    } else {
      this.receiverName = '';
      this.receiverPhone = sub.default_receiver_phone || '';
      this.selectedDate = null;
      this.selectedSlot = null;
      if (sub.lat && sub.lng) {
        this.lat = Number(sub.lat);
        this.lng = Number(sub.lng);
      } else {
        this.lat = -34.6037;
        this.lng = -58.3816;
      }
    }
  }

  tryInitMap(): void {
    if (this.hasDebt || this.loading || this.error || this.success) return;
    if (!this.mapContainer?.nativeElement) {
      if (this.mapInitAttempts < 15) {
        this.mapInitAttempts++;
        setTimeout(() => this.tryInitMap(), 150);
      }
      return;
    }
    if (typeof google === 'undefined' || !google.maps) {
      if (this.mapInitAttempts < 25) {
        this.mapInitAttempts++;
        setTimeout(() => this.tryInitMap(), 200);
      }
      return;
    }

    // Si el DOM del mapa se recreó (p.ej. al volver de "Ver / reprogramar"), reiniciar.
    this.map = null;
    this.marker = null;

    this.geocoder = new google.maps.Geocoder();
    this.map = new google.maps.Map(this.mapContainer.nativeElement, {
      center: { lat: this.lat, lng: this.lng },
      zoom: 16,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false
    });

    this.marker = new google.maps.Marker({
      position: { lat: this.lat, lng: this.lng },
      map: this.map,
      draggable: this.canEditContactAndMap,
      title: this.canEditContactAndMap
        ? 'Arrastrá el marcador hasta la ubicación exacta'
        : 'La ubicación no se puede modificar fuera de plazo'
    });

    this.marker.addListener('dragend', () => {
      this.zone.run(() => {
        const pos = this.marker.getPosition();
        this.lat = pos.lat();
        this.lng = pos.lng();
        // Solo coords: no reverse-geocode para no pisar el texto de la dirección instalada.
        this.cdr.markForCheck();
      });
    });

    this.mapReady = true;
    const hasCoords =
      this.selectedSubscription?.lat ||
      this.selectedSubscription?.active_booking?.lat;
    if (!hasCoords && this.verifiedAddress) {
      this.geocodeAddress(this.verifiedAddress);
    }

    setTimeout(() => {
      if (this.map && google.maps.event) {
        google.maps.event.trigger(this.map, 'resize');
        this.map.setCenter({ lat: this.lat, lng: this.lng });
      }
    }, 200);
  }

  updateMarkerPosition(center: boolean): void {
    if (!this.map || !this.marker) return;
    const pos = { lat: this.lat, lng: this.lng };
    this.marker.setPosition(pos);
    if (center) {
      this.map.setCenter(pos);
      this.map.setZoom(16);
    }
  }

  geocodeAddress(address: string): void {
    if (!this.geocoder || !address) return;
    this.geocoder.geocode({ address: address + ', Argentina' }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location;
        this.lat = loc.lat();
        this.lng = loc.lng();
        // No pisar el texto de la dirección instalada con el formatted_address de Google.
        this.updateMarkerPosition(true);
      }
    });
  }

  prevMonth(): void {
    this.currentMonth = new Date(
      this.currentMonth.getFullYear(),
      this.currentMonth.getMonth() - 1,
      1
    );
  }

  nextMonth(): void {
    this.currentMonth = new Date(
      this.currentMonth.getFullYear(),
      this.currentMonth.getMonth() + 1,
      1
    );
  }

  selectDate(cell: any): void {
    if (!cell?.inMonth || !cell.meta || cell.meta.disabled || this.hasDebt) {
      return;
    }
    this.formError = null;
    this.selectedDate = cell.date;
    this.selectedSlot = null;
  }

  selectSlot(slot: string): void {
    this.selectedSlot = slot;
  }

  get availableSlots(): string[] {
    if (!this.selectedDate) return [];
    const day = this.calendar.find((c) => c.date === this.selectedDate);
    return day?.slots || [];
  }

  canConfirm(): boolean {
    return !this.confirmBlockedReason();
  }

  /** Motivo por el que aún no se puede confirmar (o null si está listo). */
  confirmBlockedReason(): string | null {
    if (this.submitting) {
      return 'Estamos agendando tu visita…';
    }
    if (this.hasDebt) {
      return 'No podés agendar mientras tengas deuda pendiente.';
    }
    if (this.isVisitLocked) {
      return 'Esta visita ya está ruteada. No se puede cancelar, reprogramar ni editar. Escribinos por WhatsApp si necesitás ayuda.';
    }
    if (!this.selectedSubscriptionId || !this.selectedSubscription) {
      return 'Elegí la máquina a la que corresponde la visita.';
    }
    if (
      this.selectedSubscription.can_book === false &&
      !this.selectedSubscription.active_booking
    ) {
      const from = this.selectedSubscription.available_from;
      return from
        ? `Esta máquina se puede agendar a partir del ${this.formatDateLabel(from)}.`
        : 'Esta máquina todavía no está habilitada para agendar.';
    }
    if (!this.receiverName?.trim()) {
      return 'Completá el nombre de quién nos recibe.';
    }
    if (!this.receiverPhone?.trim()) {
      return 'Completá el teléfono de contacto.';
    }
    if (this.lat == null || this.lng == null || isNaN(Number(this.lat)) || isNaN(Number(this.lng))) {
      return 'Verificá la dirección en el mapa (arrastrá el marcador si hace falta).';
    }
    if (!this.selectedDate) {
      return 'Elegí un día en el calendario.';
    }
    if (!this.selectedSlot) {
      return 'Elegí una franja horaria.';
    }

    return null;
  }

  private formatDateLabel(iso: string): string {
    try {
      return new Date(iso + 'T12:00:00').toLocaleDateString('es-AR');
    } catch {
      return iso;
    }
  }

  confirm(): void {
    const reason = this.confirmBlockedReason();
    if (reason) {
      this.formError = reason;
      return;
    }

    const isModify = !!this.activeBooking;
    const dateLabel = this.selectedDate
      ? this.formatDateLabel(this.selectedDate)
      : '';
    const slotLabel = this.selectedSlot ? ` · ${this.selectedSlot} hs` : '';

    this.submitting = true;
    this.formError = null;
    this.error = null;
    this.scheduleService
      .book(this.token, {
        subscription_id: Number(this.selectedSubscriptionId),
        date: this.selectedDate,
        slot: this.selectedSlot,
        receiver_name: this.receiverName.trim(),
        receiver_phone: this.receiverPhone.trim(),
        lat: Number(this.lat),
        lng: Number(this.lng),
        verified_address: this.verifiedAddress
      })
      .subscribe({
        next: (res) => {
          this.success = true;
          this.calendar = res.calendar || this.calendar;
          this.bookings = res.booking
            ? [
                res.booking,
                ...this.bookings.filter(
                  (b) => b.subscription_id !== res.booking.subscription_id
                )
              ]
            : this.bookings;
          this.submitting = false;
          this.cdr.detectChanges();
          this.scrollToTop();
          Swal.fire({
            title: isModify ? 'Visita actualizada' : 'Visita agendada',
            html: dateLabel
              ? `Tu visita quedó para el <strong>${dateLabel}${slotLabel}</strong>.`
              : 'Tu visita quedó confirmada correctamente.',
            icon: 'success',
            confirmButtonColor: '#1e3a8a',
            confirmButtonText: 'Cerrar',
            didOpen: () => this.scrollToTop()
          }).then(() => this.scrollToTop());
        },
        error: (err) => {
          this.formError =
            err?.error?.error ||
            err?.error?.message ||
            'No se pudo agendar la visita. Probá de nuevo.';
          this.submitting = false;
          Swal.fire({
            title: isModify ? 'No se pudo guardar' : 'No se pudo agendar',
            text: this.formError,
            icon: 'error',
            confirmButtonColor: '#1e3a8a',
            confirmButtonText: 'Cerrar'
          });
          if (err?.status === 409) {
            this.load();
          }
        }
      });
  }

  private scrollToTop(): void {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    } catch {
      // ignore
    }
  }

  cancelVisit(): void {
    const booking = this.activeBooking;
    if (!booking?.id || !this.canCancelVisit || this.cancelling) {
      return;
    }

    const dateLabel = booking.scheduled_date
      ? new Date(booking.scheduled_date + 'T12:00:00').toLocaleDateString('es-AR')
      : '';
    const slotLabel = booking.slot ? ` · ${booking.slot} hs` : '';

    Swal.fire({
      title: '¿Cancelar esta visita?',
      html: dateLabel
        ? `Vas a cancelar la visita del <strong>${dateLabel}${slotLabel}</strong>. Después vas a poder agendar otra fecha.`
        : 'Después vas a poder agendar otra fecha.',
      icon: 'warning',
      showCancelButton: true,
      focusCancel: true,
      confirmButtonText: 'Sí, cancelar',
      cancelButtonText: 'Volver',
      confirmButtonColor: '#b91c1c',
      cancelButtonColor: '#1e3a8a',
      reverseButtons: true
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      this.cancelling = true;
      this.formError = null;
      this.scheduleService.cancel(this.token, Number(booking.id)).subscribe({
        next: (res) => {
          this.subscriptions = res.subscriptions || this.subscriptions;
          this.bookings = res.bookings || [];
          this.calendar = res.calendar || this.calendar;
          this.selectedDate = null;
          this.selectedSlot = null;
          this.applySubscriptionDefaults();
          this.cancelling = false;
          this.cdr.detectChanges();
          Swal.fire({
            title: 'Visita cancelada',
            text: 'Ya podés agendar una nueva fecha cuando quieras.',
            icon: 'success',
            confirmButtonColor: '#1e3a8a',
            confirmButtonText: 'Cerrar'
          });
        },
        error: (err) => {
          this.cancelling = false;
          Swal.fire({
            title: 'No se pudo cancelar',
            text:
              err?.error?.error ||
              err?.error?.message ||
              'Probá de nuevo o escribinos por WhatsApp.',
            icon: 'error',
            confirmButtonColor: '#1e3a8a',
            confirmButtonText: 'Cerrar'
          });
        }
      });
    });
  }
}
